import React, { FC, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { show } from '@intercom/messenger-js-sdk';

import { Button } from 'components/ui/button';
import { Skeleton } from 'components/ui/skeleton';
import { PageHeader } from 'components/page/page-header';
import { ChatIcon, ExternalIcon, MailIcon, SupportIcon } from 'components/ob-icons';

import { SupportLink } from '../../types/support.types';
import {
  clearSupportLinksCache,
  fetchSupportLinks,
  getIntercomFallbackLinks,
} from './api';

export const SupportPage: FC = () => {
  const { t: translation } = useTranslation();
  const [supportLinks, setSupportLinks] = useState<SupportLink[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  const loadSupportLinks = useCallback(async (options?: { skipCache?: boolean }) => {
    setIsLoading(true);
    setHasError(false);

    if (options?.skipCache) {
      clearSupportLinksCache();
    }

    try {
      const links = await fetchSupportLinks();
      setSupportLinks(links);
    } catch {
      setHasError(true);
      setSupportLinks(getIntercomFallbackLinks());
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSupportLinks();
  }, [loadSupportLinks]);

  const handleSupportLinkClick = (link: SupportLink) => {
    if (link.type === 'external') {
      window.open(link.url, '_blank', 'noopener,noreferrer');

      return;
    }

    if (link.type === 'email') {
      window.location.href = `mailto:${link.email}`;

      return;
    }

    show();
  };

  const handleRetry = () => {
    loadSupportLinks({ skipCache: true });
  };

  const renderLinkIcon = (link: SupportLink) => {
    const iconClassName = 'h-5 w-5 shrink-0 text-ob-text-subtle';

    if (link.type === 'external') {
      return <ExternalIcon className={iconClassName} />;
    }

    if (link.type === 'email') {
      return <MailIcon className={iconClassName} />;
    }

    return <ChatIcon className={iconClassName} />;
  };

  return (
    <div className='px-4 py-8 md:px-12 md:pb-16 md:pt-12'>
      <PageHeader
        icon={SupportIcon}
        title={translation('supportPage.title')}
        description={translation('supportPage.description')}
      />

      <div className='mt-8 grid gap-4'>
        {isLoading ? (
          <>
            <Skeleton className='h-24 w-full rounded-[14px] bg-ob-raised' />
            <Skeleton className='h-24 w-full rounded-[14px] bg-ob-raised' />
            <Skeleton className='h-24 w-full rounded-[14px] bg-ob-raised' />
          </>
        ) : (
          <>
            {hasError && (
              <div className='flex items-center justify-between gap-4 rounded-[14px] border border-ob-danger/50 bg-ob-danger/10 px-6 py-4'>
                <p className='text-sm text-ob-text-muted'>
                  {translation('supportPage.failed to load support links')}
                </p>
                <Button variant='ob-outline' size='pill-xs' onClick={handleRetry}>
                  {translation('supportPage.retry')}
                </Button>
              </div>
            )}
            {supportLinks.map((link) => (
              <button
                key={link.titleKey}
                type='button'
                className='flex min-h-24 w-full items-center justify-between gap-6 rounded-[14px] border border-ob-border bg-ob-raised px-6 py-5 text-left transition-colors duration-150 hover:border-ob-border-hover hover:bg-ob-active focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ob-focus active:translate-y-px'
                onClick={() => handleSupportLinkClick(link)}
              >
                <span className='grid gap-[7px]'>
                  <strong className='text-[17px] font-medium text-ob-text'>{translation(link.titleKey)}</strong>
                  <small className='text-[15px] text-ob-text-subtle'>{translation(link.descriptionKey)}</small>
                </span>
                {renderLinkIcon(link)}
              </button>
            ))}
          </>
        )}
      </div>
    </div>
  );
};
